<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Player } from '@maveio/components/vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import type {
  MavePlayerComponentInstance,
  MaveVideoAutoplay,
  MaveVideoExposed,
  MaveVideoProps,
} from './mave-video';
import { MAVE_VIDEO_CONTROLS_WITH_TIME_AND_SUBTITLES } from './mave-video';
import { useMavePlayerVideoState } from './use-mave-player-video-state';

// The controls property will be overwritten by the settings made in the Mave dashboard
const props = withDefaults(defineProps<MaveVideoProps>(), {
  autoplayMuted: false,
  autoplay: false,
  controls: MAVE_VIDEO_CONTROLS_WITH_TIME_AND_SUBTITLES,
  compactSubtitlesOnMobile: false,
});

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'is-running-change': [isRunning: boolean];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'sound-enabled-by-user-change': [hasUserEnabledSound: boolean];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'video-ended': [];
}>();

const mavePlayerRef = ref<MavePlayerComponentInstance | null>(null);
const {
  isVideoRunning,
  hasUserEnabledSound,
  hasVideoEnded,
  isPlayerReady,
  refresh,
} = useMavePlayerVideoState(mavePlayerRef, {
  compactSubtitlesOnMobile: props.compactSubtitlesOnMobile,
});

const playerAutoplay = computed((): MaveVideoAutoplay | undefined => {
  if (props.autoplayMuted) {
    return 'always';
  }

  if (!props.autoplay) {
    return undefined;
  }

  return props.autoplay;
});

const playerMuted = computed(() => props.autoplayMuted);

watch(
  mavePlayerRef,
  (player) => {
    if (player && props.autoplayMuted) {
      player.muted = true;
    }
  },
  { immediate: true },
);

watch(
  isVideoRunning,
  (isRunning) => {
    emit('is-running-change', isRunning);
  },
  { immediate: true },
);

watch(
  hasUserEnabledSound,
  (enabled) => {
    emit('sound-enabled-by-user-change', enabled);
  },
  { immediate: true },
);

watch(hasVideoEnded, (ended) => {
  if (ended) {
    emit('video-ended');
  }
});

defineExpose<MaveVideoExposed>({
  refreshRunningState: refresh,
  isVideoRunning,
  hasUserEnabledSound,
});
</script>

<template>
  <div class="w-full overflow-hidden rounded-lg shadow-md">
    <div class="relative aspect-video w-full bg-neutral-100">
      <div
        class="absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-500 ease-out"
        :class="isPlayerReady ? 'pointer-events-none opacity-0' : 'opacity-100'"
      >
        <HugeIcon icon="play" class="text-neutral-300" :size="48" />
      </div>

      <div
        class="absolute inset-0 transition-opacity duration-500 ease-out"
        :class="isPlayerReady ? 'opacity-100' : 'opacity-0'"
      >
        <Player
          ref="mavePlayerRef"
          class="h-full w-full"
          :embed="props.videoId"
          :autoplay="playerAutoplay"
          :controls="props.controls"
          :muted="playerMuted"
          :subtitles="props.subtitles"
          :active-subtitle="props.activeSubtitle"
        />
      </div>
    </div>
  </div>
</template>
